import { AutoAwesome } from '@mui/icons-material';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  GaugeContainer,
  gaugeClasses,
  GaugeReferenceArc,
  GaugeValueArc,
  useGaugeState,
} from '@mui/x-charts/Gauge';
import api from '~/api/Endpoints';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import { useFreeMonthlyCredits } from '~/hooks/useFreeMonthlyCredits';
import { useQuery } from '~/hooks/useQuery';
import { useScreenSize } from '~/hooks/useScreenSize';
import { useUser } from '~/hooks/useUser';
import { useHandleException } from '~/scripts/handleException';
import type { GetApiAccessResponse } from '@shared/types/api-access';

const GAUGE_SIZE = 160;

const GaugeCenterLabel = () => {
  const theme = useTheme();
  const { cx, cy, value } = useGaugeState();
  return (
    <g>
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        fontSize={32}
        fontWeight={700}
        fill={theme.palette.text.primary}>
        {value?.toLocaleString()}
      </text>
      <text
        x={cx}
        y={cy + 22}
        textAnchor="middle"
        fontSize={13}
        fill={theme.palette.text.secondary}>
        credits left
      </text>
    </g>
  );
};

export const ApiFreeTierCard = () => {
  const { mdAndUp } = useScreenSize();
  const user = useUser();
  const handleException = useHandleException();
  const freeMonthlyCredits = useFreeMonthlyCredits();
  const apiAccessRequest = useQuery<GetApiAccessResponse | null>({
    queryKey: [user?.uuid || 'guest'],
    queryFn: async () => {
      if (user?.isGuest) {
        return null;
      }
      return api.get('/api-access');
    },
    onError(error) {
      handleException(error, { category: 'api-access' });
    },
  });
  const isLoading =
    !user?.isGuest && !apiAccessRequest.data && !apiAccessRequest.isError;
  const credits = apiAccessRequest.data?.credits;
  const monthlyCredits = credits?.monthly_credits ?? freeMonthlyCredits;
  const remainingCredits = credits?.remaining ?? monthlyCredits;
  const creditsUsed = monthlyCredits - remainingCredits;

  return (
    <Card variant="outlined">
      <Stack
        direction={mdAndUp ? 'row' : 'column'}
        divider={
          <Divider orientation={mdAndUp ? 'vertical' : 'horizontal'} flexItem />
        }>
        <Box sx={{ flex: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 6 } }}>
            <Typography variant="overline" color="primary">
              New · API Pricing
            </Typography>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <AutoAwesome
                htmlColor="gold"
                fontSize={'large'}
                sx={{ verticalAlign: 'middle' }}
              />{' '}
              Free API credits for everyone.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Every account now gets {monthlyCredits.toLocaleString()} API and
              MCP credits a month for code search, domain lookups, and more.
              Only pay for usage beyond this.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                disableElevation
                href={
                  user.isGuest ? '/signup?reason=api.access' : '/api#apikey'
                }>
                {user.isGuest ? 'Register Free' : 'View API Key'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="/api#pricing"
                preventScrollReset>
                See pricing
              </Button>
            </Stack>
          </CardContent>
        </Box>
        <Stack
          spacing={4}
          justifyContent="center"
          sx={{ flex: 2, bgcolor: 'grey.50', p: { xs: 3, md: 6 } }}>
          <Stack
            direction="row"
            spacing={4}
            alignItems="center"
            justifyContent="center">
            {isLoading ? (
              <Skeleton
                variant="circular"
                width={GAUGE_SIZE}
                height={GAUGE_SIZE}
              />
            ) : (
              <GaugeContainer
                width={GAUGE_SIZE}
                height={GAUGE_SIZE}
                value={Math.max(remainingCredits, 0)}
                valueMax={monthlyCredits}
                innerRadius="84%"
                cornerRadius="50%"
                sx={(theme) => {
                  return {
                    [`& .${gaugeClasses.referenceArc}`]: {
                      fill: alpha(theme.palette.primary.main, 0.12),
                    },
                  };
                }}>
                <GaugeReferenceArc />
                <GaugeValueArc />
                <GaugeCenterLabel />
              </GaugeContainer>
            )}
            <Stack spacing={1.5}>
              <div>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  lineHeight={1.5}>
                  Used
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    <>
                      {creditsUsed.toLocaleString()} of{' '}
                      {monthlyCredits.toLocaleString()}
                    </>
                  )}
                </Typography>
              </div>
              <div>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  lineHeight={1.5}>
                  Resets
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {isLoading ? (
                    <Skeleton width={140} />
                  ) : credits?.reset_at ? (
                    new Date(credits.reset_at).toLocaleString()
                  ) : (
                    'next month'
                  )}
                </Typography>
              </div>
              <Link href="/api#apikey" variant="body2" underline="always">
                View usage history
              </Link>
            </Stack>
          </Stack>
          <Divider />
          <Typography variant="body2" color="text.secondary">
            Need more than {monthlyCredits.toLocaleString()}? Additional
            requests are billed pay-as-you-go, only for what you use.{' '}
            <Link href="/api#pricing" underline="always" preventScrollReset>
              See rates
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};
